import React, {useEffect, useRef, useState} from 'react';

import {
  DateFilter,
  FieldSelect,
  OperatorsSelect,
  SelectFilter,
  TextFilter,
  MultiSelectFilter,
} from './components';
import {SelectDataProps} from 'shared/types/select-data';

import {
  DateFilterProps,
  FilterOptionProps,
  FilterProps,
  FilterValueProps,
  Operators,
} from '../../types';

import {Container, Remove} from './styles';

interface Props {
  filters: FilterOptionProps[];
  remove: () => void;
  confirm: (filter: FilterProps, initialField?: string) => void;
  initialFilter?: FilterProps;
  activeFilters: string[];
}

const FilterOption: React.FC<Props> = ({
  filters,
  remove,
  confirm,
  initialFilter,
  activeFilters,
}) => {
  const [value, setValue] = useState<FilterValueProps | undefined>(
    initialFilter?.value,
  );
  const [field, setField] = useState<SelectDataProps | undefined>(
    initialFilter?.field,
  );
  const [operator, setOperator] = useState<Operators | undefined>(
    initialFilter?.operator,
  );
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    if (initialFilter?.value === false || initialFilter?.value)
      setValue(initialFilter?.value);
  }, [initialFilter]);

  const filter = filters.find((filter) => filter.field === field?.value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (getOperatorType() === 'string' && !initialFilter)
      setOperator(Operators.Contains);
  }, [filter]);

  const fields = filters
    .filter((filter) => !activeFilters.includes(filter.field))
    .map((filter) => ({
      label: filter.label,
      value: filter.field,
    }));

  const [showField, setShowField] = useState(false);
  const [showOperator, setShowOperator] = useState(false);

  useEffect(() => {
    if (!field) setShowField(true);
  }, []);

  const onFieldChange = (someField: SelectDataProps) => {
    setField(someField);
    if (field && someField !== field) setValue(undefined);

    setShowField(false);
    inputRef.current?.focus();
    setOpened(true);
  };

  const onOperatorChange = (operator: Operators) => {
    setOperator(operator);
    setShowOperator(false);
    if (value) return;

    inputRef.current?.focus();
    setOpened(true);
  };

  const handleConfirm = (value: FilterValueProps) => {
    if (!field || (!value && value !== false && value !== 0) || !operator)
      return;

    confirm({field, value, operator}, initialFilter?.field.value);
    setOpened(false);
  };

  const renderFilter = () => {
    if (!filter) return;
    if (filter.type === 'text')
      return (
        <TextFilter
          {...{
            ref: inputRef,
            handleConfirm,
            value: value as string,
          }}
        />
      );
    if (filter.type === 'select')
      return (
        <SelectFilter
          {...{
            handleConfirm,
            opened,
            setOpened,
            options: filter.values ?? [],
            value: value as string,
            search: filter.search,
          }}
        />
      );
    if (filter.type === 'multi-select')
      return (
        <MultiSelectFilter
          {...{
            handleConfirm,
            opened,
            setOpened,
            options: filter.values ?? [],
            values: value as string[],
          }}
        />
      );
    if (filter.type === 'date')
      return (
        <DateFilter
          {...{
            handleConfirm,
            opened,
            setOpened,
            value: value as DateFilterProps[],
            dateInterval: filter?.dateInterval,
          }}
        />
      );
  };

  const getOperatorType = () => {
    if (filter?.type === 'text' && !filter?.valueAsNumber) return 'string';
    if (filter?.type === 'text' && filter?.valueAsNumber) return 'numeric';
    return 'date';
  };

  return (
    <Container>
      <FieldSelect
        opened={showField}
        open={() => setShowField(true)}
        close={() => setShowField(false)}
        onChange={onFieldChange}
        {...{fields, value: field}}
      />
      {field && (
        <OperatorsSelect
          value={operator}
          opened={showOperator}
          onChange={onOperatorChange}
          open={() => setShowOperator(true)}
          close={() => setShowOperator(false)}
          operatorType={getOperatorType()}
        />
      )}
      {renderFilter()}
      <Remove onClick={remove} />
    </Container>
  );
};

export default FilterOption;
